// Encoder/decoder del binario `.epcp` del parque (NÚCLEO ESTABLE, puro).
//
// No conoce el motor ni Vue. Convierte la GEOMETRÍA + CAPAS de una vista a/desde
// un ArrayBuffer. El decode deja la geometría y la identidad como typed arrays
// ZERO-COPY (vistas directas sobre el buffer), listas para alimentar el sceneFunc
// de Konva hoy o un InstancedMesh de Three.js mañana. Ver data/binary-format.js.

import {
  EPCP_MAGIC, EPCP_VERSION,
  HEADER_BYTES, DIR_ENTRY_BYTES, GEOM_BYTES, ID_BYTES, LAYER_BYTES, NO_LAYER,
} from '../data/binary-format'

const utf8 = {
  enc: new TextEncoder(),
  dec: new TextDecoder(),
}

/**
 * Codifica una vista a ArrayBuffer `.epcp`.
 *
 * @param {Object} view
 * @param {number} view.viewCode  0=structures,1=pilings,2=modules
 * @param {Array}  view.layers    [{ name, color }]  color = entero RGB (como en el JSON de capas)
 * @param {Array}  view.pictograms [{ pictogramId, geomVersion, entities: [{ id, x, y, width, height, layer }] }]
 * @returns {ArrayBuffer}
 */
export function encodeView({ viewCode = 0, layers = [], pictograms = [] }) {
  // Mapa nombre de capa -> índice en la tabla.
  const layerIndex = new Map()
  layers.forEach((l, i) => layerIndex.set(l.name, i))

  const totalEntities = pictograms.reduce((acc, p) => acc + (p.entities ? p.entities.length : 0), 0)
  const picCount = pictograms.length

  // Tabla de capas: layerCount u16 + por capa (color u32 + nameLen u8 + name utf8).
  const layerNameBytes = layers.map(l => utf8.enc.encode(l.name || ''))
  const tableBytes = 2 + layerNameBytes.reduce((acc, nb) => acc + 4 + 1 + nb.length, 0)

  const geomOffset = HEADER_BYTES + DIR_ENTRY_BYTES * picCount
  const idOffset = geomOffset + GEOM_BYTES * totalEntities
  const layerOffset = idOffset + ID_BYTES * totalEntities
  const tableOffset = layerOffset + LAYER_BYTES * totalEntities
  const totalBytes = tableOffset + tableBytes

  const buffer = new ArrayBuffer(totalBytes)
  const dv = new DataView(buffer)

  // --- Header ---
  for (let i = 0; i < 4; i++) dv.setUint8(i, EPCP_MAGIC.charCodeAt(i))
  dv.setUint16(4, EPCP_VERSION, true)
  dv.setUint8(6, viewCode)
  dv.setUint8(7, 0) // flags
  dv.setUint16(8, picCount, true)
  dv.setUint16(10, 0, true) // pad
  dv.setUint32(12, totalEntities, true)

  // --- Directorio + bloques (en el MISMO orden de recorrido de pictogramas/entidades) ---
  const geom = new Float32Array(buffer, geomOffset, totalEntities * 4)
  const ids = new Uint32Array(buffer, idOffset, totalEntities)
  const layerCodes = new Uint16Array(buffer, layerOffset, totalEntities)

  let dirPos = HEADER_BYTES
  let e = 0
  for (const p of pictograms) {
    const ents = p.entities || []
    dv.setUint32(dirPos, (p.pictogramId >>> 0), true)
    dv.setUint32(dirPos + 4, (p.geomVersion >>> 0), true)
    dv.setUint32(dirPos + 8, ents.length, true)
    dirPos += DIR_ENTRY_BYTES

    for (const ent of ents) {
      const g = e * 4
      geom[g] = ent.x || 0
      geom[g + 1] = ent.y || 0
      geom[g + 2] = ent.width || 0
      geom[g + 3] = ent.height || 0
      ids[e] = (ent.id >>> 0)
      const li = layerIndex.has(ent.layer) ? layerIndex.get(ent.layer) : NO_LAYER
      layerCodes[e] = li
      e++
    }
  }

  // --- Tabla de capas ---
  let t = tableOffset
  dv.setUint16(t, layers.length, true); t += 2
  for (let i = 0; i < layers.length; i++) {
    dv.setUint32(t, (layers[i].color >>> 0), true); t += 4
    const nb = layerNameBytes[i]
    dv.setUint8(t, nb.length); t += 1
    for (let k = 0; k < nb.length; k++) dv.setUint8(t + k, nb[k])
    t += nb.length
  }

  return buffer
}

/**
 * Decodifica un ArrayBuffer `.epcp`. La geometría, la identidad y las capas
 * quedan como typed arrays ZERO-COPY sobre el mismo buffer.
 *
 * @param {ArrayBuffer} buffer
 * @returns {{
 *   version:number, viewCode:number, totalEntities:number,
 *   directory: Array<{pictogramId:number, geomVersion:number, entityCount:number, entityOffset:number}>,
 *   geometry: Float32Array,   // [x,y,w,h] × N
 *   entityIds: Uint32Array,   // N
 *   layerCodes: Uint16Array,  // N
 *   layers: Array<{name:string, color:number}>
 * }}
 */
export function decodeView(buffer) {
  const dv = new DataView(buffer)

  const magic = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3))
  if (magic !== EPCP_MAGIC) {
    throw new Error('binary-scene: magic inválido (' + magic + '), no es un .epcp')
  }
  const version = dv.getUint16(4, true)
  const viewCode = dv.getUint8(6)
  const picCount = dv.getUint16(8, true)
  const totalEntities = dv.getUint32(12, true)

  // Directorio.
  const directory = []
  let dirPos = HEADER_BYTES
  let entityOffset = 0
  for (let i = 0; i < picCount; i++) {
    const pictogramId = dv.getUint32(dirPos, true)
    const geomVersion = dv.getUint32(dirPos + 4, true)
    const entityCount = dv.getUint32(dirPos + 8, true)
    directory.push({ pictogramId, geomVersion, entityCount, entityOffset })
    entityOffset += entityCount
    dirPos += DIR_ENTRY_BYTES
  }

  // Bloques zero-copy.
  const geomOffset = HEADER_BYTES + DIR_ENTRY_BYTES * picCount
  const idOffset = geomOffset + GEOM_BYTES * totalEntities
  const layerOffset = idOffset + ID_BYTES * totalEntities
  const tableOffset = layerOffset + LAYER_BYTES * totalEntities

  const geometry = new Float32Array(buffer, geomOffset, totalEntities * 4)
  const entityIds = new Uint32Array(buffer, idOffset, totalEntities)
  const layerCodes = new Uint16Array(buffer, layerOffset, totalEntities)

  // Tabla de capas.
  const layers = []
  let t = tableOffset
  const layerCount = dv.getUint16(t, true); t += 2
  for (let i = 0; i < layerCount; i++) {
    const color = dv.getUint32(t, true); t += 4
    const nameLen = dv.getUint8(t); t += 1
    const name = utf8.dec.decode(new Uint8Array(buffer, t, nameLen)); t += nameLen
    layers.push({ name, color })
  }

  return { version, viewCode, totalEntities, directory, geometry, entityIds, layerCodes, layers }
}
